import * as d3 from 'd3';
import {
  GRAPH_NODE_TYPE,
  GRAPH_NODE_DISPLAY_TEXT,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from 'constants/MogamboConstants';
import { addNewNode, addABNode } from 'actions/BotActions';
import {
  setCodeNodeCoords,
  resetCodeNodeData,
} from '@src/containers/CodeNode/store/actions';
import {
  navToNewStep,
  navToCodeStep,
  navToDynamicStep,
  navToAissStep,
} from 'actions/SubstoryActions';
import {
  setDynamicNodeCoords,
  resetDynamicNodeData,
} from '@src/containers/DynamicNode/store/actions';
import CommonStore from '@src/stores/CommonStore';
import { CONSTANTS } from './constants';

/**
 * local storage utility to store graph translation/event history
 */
export const graphEventStorage = {
  get: (storyId) => {
    const eventHistory = localStorage.getItem('eventHistory');
    return eventHistory ? JSON.parse(eventHistory)[storyId] : false;
  },
  set: (storyId, data) => {
    const eventHistory = localStorage.getItem('eventHistory');
    if (!eventHistory) {
      localStorage.setItem(
        'eventHistory',
        JSON.stringify({
          [storyId]: data,
        })
      );
    } else {
      localStorage.setItem(
        'eventHistory',
        JSON.stringify({
          ...JSON.parse(eventHistory),
          [storyId]: data,
        })
      );
    }
  },
  delete: (storyId) => {
    const eventHistory = localStorage.getItem('eventHistory');
    if (eventHistory) {
      const jsonObj = JSON.parse(eventHistory);
      if (jsonObj[storyId]) {
        delete jsonObj[storyId];
        localStorage.setItem('eventHistory', JSON.stringify(jsonObj));
      }
    }
  },
};

/**
 *
 * This function return path of a rounded rectangle.
 * It was taken off stackoverflow : https://stackoverflow.com/a/13505624
 *
 * @param {number} x x-cordinate of rectangle
 * @param {number} y y-cordinate of rectangle
 * @param {number} w width of rectangle
 * @param {number} h height of rectangle
 * @param {number} r corner radius of rectangle
 * @param {boolean} tl top left corner rounded?
 * @param {boolean} tr top right corner rounded?
 * @param {boolean} bl bottom left corner rounded?
 * @param {boolean} br bottom right corner rounded?
 */
export function roundedRectangle(x, y, w, h, r, tl, tr, bl, br) {
  let rectanglePath;
  rectanglePath = `M${x + r},${y}`;
  rectanglePath += `h${w - 2 * r}`;
  if (tr) {
    rectanglePath += `a${r},${r} 0 0 1 ${r},${r}`;
  } else {
    rectanglePath += `h${r}`;
    rectanglePath += `v${r}`;
  }
  rectanglePath += `v${h - 2 * r}`;
  if (br) {
    rectanglePath += `a${r},${r} 0 0 1 ${-r},${r}`;
  } else {
    rectanglePath += `v${r}`;
    rectanglePath += `h${-r}`;
  }
  rectanglePath += `h${2 * r - w}`;
  if (bl) {
    rectanglePath += `a${r},${r} 0 0 1 ${-r},${-r}`;
  } else {
    rectanglePath += `h${-r}`;
    rectanglePath += `v${-r}`;
  }
  rectanglePath += `v${2 * r - h}`;
  if (tl) {
    rectanglePath += `a${r},${r} 0 0 1 ${r},${-r}`;
  } else {
    rectanglePath += `v${-r}`;
    rectanglePath += `h${r}`;
  }
  rectanglePath += 'z';
  return rectanglePath;
}

export function snapToGrid(x, y) {
  const topLeft = {
    x: Math.floor(x / CONSTANTS.grid_size) * CONSTANTS.grid_size,
    y: Math.floor(y / CONSTANTS.grid_size) * CONSTANTS.grid_size,
  };

  const diff = {
    x: x - topLeft.x,
    y: y - topLeft.y,
  };

  const halfGridSize = CONSTANTS.grid_size / 2;

  const finalPos = {
    x: topLeft.x,
    y: topLeft.y,
  };

  if (diff.x > halfGridSize) {
    finalPos.x += CONSTANTS.grid_size;
  }

  if (diff.y > halfGridSize) {
    finalPos.y += CONSTANTS.grid_size;
  }

  return finalPos;
}

/*
    Finds drop position when new node is added
    1. d3 transform attribute returns a string containing translate and scale info
      from which the value were extracted
    2. Because the graph is scaled and translated, the current mouse position also
      needs to be translated and scaled
    3. Finally, the transformed coordinates are snapped to grid, to 
      find the final position
  */
export const getDropPosition = (node) => {
  const newNode = node;
  const t = d3.select('#gridcontainer').attr('transform');
  const translate = t?.split(' ')?.[0];
  const scale = t?.split(' ')?.[1];
  const coordinates = translate
    ?.substring(translate.indexOf('(') + 1, translate.indexOf(')'))
    ?.split(',');
  const scaleFactor = scale?.substring(
    scale.indexOf('(') + 1,
    scale.indexOf(')')
  );
  const x = (newNode.x - parseFloat(coordinates?.[0])) / scaleFactor;
  const y = (newNode.y - parseFloat(coordinates?.[1])) / scaleFactor;
  const draggedPos = snapToGrid(
    x - CONSTANTS.node_width / 2,
    y - CONSTANTS.node_height / 2
  );
  newNode.x = draggedPos.x;
  newNode.y = draggedPos.y;
  return newNode;
};

const createCodeNode = (x, y, meta) => {
  addNewNode(x, y, meta, (id) => {
    meta.step.id = id;
    const updatedPos = getDropPosition({
      x,
      y,
    });

    // We reset the data in code block, then update the coords using this function as it prevents setting
    // edited flag to true. Editing flag to true results in opening the RHS with save button already enabled.
    resetCodeNodeData(() => {
      setCodeNodeCoords(updatedPos.x, updatedPos.y, () => {
        navToCodeStep(meta, 'settings');
      });
    });
  });
};

const createDynamicNode = (x, y, meta) => {
  addNewNode(x, y, meta, (id) => {
    meta.step.id = id;
    const updatedPos = getDropPosition({
      x,
      y,
    });

    resetDynamicNodeData(() => {
      setDynamicNodeCoords(updatedPos.x, updatedPos.y, () => {
        navToDynamicStep(meta, 'settings');
      });
    });
  });
};

const createStaticNode = (
  x,
  y,
  meta,
  isABTestVariantNode = false,
  isGenAi,
  callback
) => {
  addNewNode(x, y, meta, (id) => {
    meta.step.id = id;
    if (isGenAi && CommonStore.getGenAiModalActive()) {
      callback(meta, x, y);
    }
    if (!isABTestVariantNode && !isGenAi) {
      navToNewStep(meta);
    }
  });
};

const createAissNode = (x, y, meta) => {
  addNewNode(x, y, meta, (id) => {
    meta.step.id = id;
    const updatedPos = getDropPosition({
      x,
      y,
    });
    navToAissStep(meta);
  });
};

const createABNode = (x, y, meta) => {
  addABNode({ x, y, ...meta });
};

export const createNewNode = (
  x,
  y,
  meta,
  isABTestVariantNode = false,
  isGenAi = false,
  callback
) => {
  switch (meta.step.type) {
    case GRAPH_NODE_DISPLAY_TEXT.CODE:
      createCodeNode(x, y, meta);
      return;
    case GRAPH_NODE_DISPLAY_TEXT.OUTPUT:
      createDynamicNode(x, y, meta);
      return;
    case GRAPH_NODE_DISPLAY_TEXT.STATIC:
      createStaticNode(x, y, meta, isABTestVariantNode, isGenAi, callback);
      return;
    case GRAPH_NODE_DISPLAY_TEXT.AISS:
      createAissNode(x, y, meta);
      return;

    case GRAPH_NODE_DISPLAY_TEXT.AB_NODE:
      createABNode(x, y, meta);

    default:
  }
};

export function getBlockData(block, color) {
  switch (block.block_type) {
    case GRAPH_NODE_TYPE.STATIC:
      return {
        id: block.block_id,
        title: block.title,
        destination_list: block.destination_list,
        source_list: block.source_list,
        x: block.position.x,
        y: block.position.y,
        step_type: {
          start: block.data.is_start,
          [CONTEXT_CLEAR_NODE.KEY]: block.data.is_end,
        },
        active: block.active,
        substory_id: block.sub_story_id,
        system_name: block.data.node_name,
        api_function: block.data.has_integration,
        color,
        subStoryId: block.sub_story_id,
        type: GRAPH_NODE_DISPLAY_TEXT.STATIC,
        [END_OF_QUERY.KEY]: block.data[END_OF_QUERY.KEY] || false,
      };

    case GRAPH_NODE_TYPE.CODE:
      return {
        id: block.block_id,
        title: block.title,
        destination_list: block.destination_list,
        source_list: block.source_list,
        x: block.position.x,
        y: block.position.y,
        step_type: {
          start: block.data.is_start,
          [CONTEXT_CLEAR_NODE.KEY]: block.data.is_end,
        },
        active: block.active,
        substory_id: block.sub_story_id,
        system_name: block.data.node_name,
        api_function: block.data.has_integration,
        color,
        subStoryId: block.sub_story_id,
        type: GRAPH_NODE_DISPLAY_TEXT.CODE,
      };

    case GRAPH_NODE_TYPE.OUTPUT:
      return {
        id: block.block_id,
        title: block.title,
        destination_list: block.destination_list,
        source_list: block.source_list,
        x: block.position.x,
        y: block.position.y,
        step_type: {
          start: block.data.is_start,
          [CONTEXT_CLEAR_NODE.KEY]: block.data.is_end,
        },
        active: block.active,
        substory_id: block.sub_story_id,
        system_name: block.data.node_name,
        api_function: block.data.has_integration,
        color,
        subStoryId: block.sub_story_id,
        type: GRAPH_NODE_DISPLAY_TEXT.OUTPUT,
        [END_OF_QUERY.KEY]: block.data[END_OF_QUERY.KEY] || false,
      };

    case GRAPH_NODE_TYPE.AB_NODE:
      return {
        id: block.block_id,
        title: block.title,
        destination_list: block.destination_list,
        source_list: block.source_list,
        x: block.position.x,
        y: block.position.y,
        step_type: {
          start: block.data.is_start,
          [CONTEXT_CLEAR_NODE.KEY]: block.data.is_end,
        },
        active: block.active,
        substory_id: block.sub_story_id,
        system_name: block.data.node_name,
        api_function: block.data.has_integration,
        color,
        subStoryId: block.sub_story_id,
        type: GRAPH_NODE_DISPLAY_TEXT.AB_NODE,
        [END_OF_QUERY.KEY]: block.data[END_OF_QUERY.KEY] || false,
      };

    case 'aiss_intent':
      return {
        id: block.block_id,
        title: block.title,
        destination_list: block.destination_list,
        source_list: block.source_list,
        x: block.position.x,
        y: block.position.y,
        step_type: {
          start: block.data.is_start,
          [CONTEXT_CLEAR_NODE.KEY]: false,
        },
        active: block.active,
        substory_id: block.sub_story_id,
        system_name: block.data.node_name,
        api_function: block.data.has_integration,
        color,
        subStoryId: block.sub_story_id,
        type: GRAPH_NODE_DISPLAY_TEXT.AISS,
        [END_OF_QUERY.KEY]: false,
      };

    default:
      return null;
  }
}

/**
 * Gets the warning text for a particular node
 * @param {*} nodeData Data of the node
 */
export function getWarningInfo(nodeData) {
  if (nodeData) {
    const { type, destinationList, endNode } = nodeData;
    let retData = {};
    let msg = '';
    let displayWarningIcon = false;

    // Static & Output Node
    if (
      type === GRAPH_NODE_DISPLAY_TEXT.STATIC ||
      type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT
    ) {
      if (destinationList.length === 0 && !endNode) {
        msg = 'Please add a connection or mark it as End of Query.';
        displayWarningIcon = true;
      }
    }

    // Code Node
    if (type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
      if (destinationList.length === 0) {
        msg = 'Please connect to an Output Step.';
        displayWarningIcon = true;
      }
    }

    retData = {
      msg,
      displayWarningIcon,
    };

    return retData;
  }
  return null;
}
